import { useCallback, useEffect, useState } from 'react'

import { httpStatuses, useFetch } from '@reveni-io/reveni-ui'

import { contentTabs } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/ContentTabs/constants'
import { editorModes } from '@/configuration/pages/ConfigurationPage/CommunicationsConfiguration/EmailTemplates/EmailTemplateDetailConfiguration/EditorModeSelector/constants'
import { configurationTypes } from '@/configuration/pages/ConfigurationPage/constants'
import { getEntityIdByConfigurationMode } from '@/configuration/pages/ConfigurationPage/helpers'

import { servicesByConfigurationMode } from './constants'
import { transformResponseLiquidPreviewError, transformResponsePreview } from './transformers'

const usePreview = ({
  subjectText,
  content,
  language,
  mode,
  setToastInfo,
  selectedContentTab,
  configurationMode,
  merchantId,
  blocks,
  returnPortalId,
}) => {
  const services = servicesByConfigurationMode[configurationMode || configurationTypes.returnsPortal]
  const entityId = getEntityIdByConfigurationMode({ configurationMode, merchantId, returnPortalId })
  const [previewedLanguage, setPreviewedLanguage] = useState(null)
  const [liquidPreviewError, setLiquidPreviewError] = useState(null)
  const {
    data: preview,
    loading: fetchingPreview,
    fetchDataCancellable: fetchPreview,
    cancel: cancelPreview,
    setData: setPreview,
  } = useFetch(services.getPreviewEmailTemplate, {
    doInitialRequest: false,
    transformResponse: transformResponsePreview,
  })
  const languageToPreview = selectedContentTab === contentTabs.preview ? language : null
  const isLiquidContentEmpty = mode === editorModes.liquid && !content.trim()

  const onFetchPreview = useCallback(async () => {
    const payload = { subject_text: subjectText, content, language, mode }

    if (mode === editorModes.html) payload.blocks = blocks

    try {
      await fetchPreview({ ...entityId, payload })
    } catch (error) {
      if (mode === editorModes.liquid && error.response?.status === httpStatuses.unprocessableEntity) {
        setLiquidPreviewError(transformResponseLiquidPreviewError(error.response.data))
        return
      }

      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }, [fetchPreview, entityId, subjectText, content, language, mode, blocks, setToastInfo])

  useEffect(() => {
    if (previewedLanguage === languageToPreview) return

    cancelPreview()
    setPreview(null)
    setLiquidPreviewError(null)
    setPreviewedLanguage(languageToPreview)

    if (languageToPreview && !isLiquidContentEmpty) onFetchPreview()
  }, [cancelPreview, isLiquidContentEmpty, languageToPreview, onFetchPreview, previewedLanguage, setPreview])

  return { preview, fetchingPreview, liquidPreviewError, isLiquidContentEmpty }
}

export default usePreview
